import { useCubeQuery } from '@cubejs-client/react';
import { LocationOnOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { ReactNode } from 'react';

import { DimSupplierField } from '~/common/types/cubes/DimSupplier';
import { SupplierAddressField } from '~/common/types/cubes/SupplierAddress';
import { SupplierSourceSystemField } from '~/common/types/cubes/SupplierSourceSystem';
import { BaseDetailHeader, BaseDetailHeaderProps } from '~/components/BaseDetailHeader/BaseDetailHeader';
import { LocationLink } from '~/components/LocationLink/LocationLink';

import { Address } from './SupplierDetailHeader.styled';

export interface SupplierDetailHeaderProps {
  id: string;
  tabs?: BaseDetailHeaderProps['tabs'];
  actionsRight?: ReactNode;
}

export const SupplierDetailHeader = ({ id, tabs, actionsRight }: SupplierDetailHeaderProps) => {
  const nameQuery = useCubeQuery({
    dimensions: [DimSupplierField.NAME],
    filters: [{ member: DimSupplierField.ID, operator: 'equals', values: [id] }],
  });
  const linksQuery = useCubeQuery({
    dimensions: [
      SupplierSourceSystemField.SOURCE_REFERENCE,
      SupplierSourceSystemField.LINK,
      SupplierSourceSystemField.SOURCE_SYSTEM,
    ],
    filters: [{ member: SupplierSourceSystemField.INTERNAL_ID, operator: 'equals', values: [id] }],
  });
  const addressQuery = useCubeQuery({
    dimensions: [
      SupplierAddressField.CITY,
      SupplierAddressField.STREET,
      SupplierAddressField.POSTAL_CODE,
      SupplierAddressField.COUNTRY_CODE,
    ],
    filters: [{ member: SupplierAddressField.SUPPLIER_ID, operator: 'equals', values: [id] }],
  });

  const loading = nameQuery.isLoading || linksQuery.isLoading || addressQuery.isLoading;
  const name = (nameQuery.resultSet?.rawData()?.[0]?.[DimSupplierField.NAME] as string) ?? '';

  const linkRows = linksQuery.resultSet?.rawData() ?? [];
  const links = linkRows.map((row) => ({
    title: `${row[SupplierSourceSystemField.SOURCE_REFERENCE] ?? ''}`,
    url: `${row[SupplierSourceSystemField.LINK] ?? ''}`,
  }));

  const addressRow = addressQuery.resultSet?.rawData()?.[0];
  const address = addressRow
    ? {
        city: (addressRow[SupplierAddressField.CITY] as string) ?? null,
        street: (addressRow[SupplierAddressField.STREET] as string) ?? null,
        postalCode: (addressRow[SupplierAddressField.POSTAL_CODE] as string) ?? null,
        countryCode: (addressRow[SupplierAddressField.COUNTRY_CODE] as string) ?? null,
      }
    : undefined;

  return (
    <BaseDetailHeader
      loading={loading}
      title={name}
      tabs={tabs}
      links={links}
      actionsRight={actionsRight}
      center={
        <Stack spacing={3} direction="row" sx={{ overflow: 'hidden' }}>
          {address && (
            <Address
              variant="body2"
              color="textPrimary"
              startIcon={<LocationOnOutlined color="inherit" fontSize="small" />}
            >
              <LocationLink value={address} />
            </Address>
          )}
        </Stack>
      }
    />
  );
};
